/**
 * The keyboard, acted on. `lib/shortcuts.js` says which key is which; this
 * listens for them on the page and does what each means. What happens inside
 * a sheet — Esc, ⌘/Ctrl+Enter, keeping Tab inside it — is `ui/Sheet.vue`'s.
 *
 * The page's shortcuts are single keys, so they only fire when:
 *   - nothing is being typed into (a letter in a field is a letter),
 *   - no sheet or date range is open (they have the keyboard then), and
 *   - no modifier is held (Ctrl+N, ⌘+E and the rest are the browser's).
 */
import { state, openSheet, stopSelecting } from './state'
import { showMonth, viewedMonth } from './index'
import { cyclePrivacy } from './settings'
import { setTheme } from './theme'
import { openHelp } from './help'
import { exportCsv } from './export'
import { SHORTCUTS, isTyping, matches } from '../lib/shortcuts'
import { shiftMonth } from '../lib/dates'
import { count } from './usage'

/** The rows of the list that can be opened, in order. */
const rows = () => [...document.querySelectorAll('button[data-tx]:not([disabled])')]

/** Moves the keyboard to the next (1) or previous (-1) transaction, from wherever it is. */
function stepRow(step) {
  const list = rows()
  if (!list.length) return
  const at = list.indexOf(document.activeElement)
  const next = at === -1 ? (step > 0 ? 0 : list.length - 1) : Math.min(list.length - 1, Math.max(0, at + step))
  list[next].focus()
  list[next].scrollIntoView({ block: 'nearest' })
}

/** What each page shortcut does. */
export const ACTIONS = {
  new: () => openSheet('transaction'),
  search: () => document.querySelector('[data-search]')?.focus(),
  nextRow: () => stepRow(1),
  prevRow: () => stepRow(-1),
  range: () => (state.ui.rangeOpen = true),
  select: () => (state.ui.selecting ? stopSelecting() : (state.ui.selecting = true)),
  prevMonth: () => showMonth(shiftMonth(viewedMonth.value, -1)),
  nextMonth: () => showMonth(shiftMonth(viewedMonth.value, 1)),
  privacy: () => cyclePrivacy(),
  theme: () => setTheme(state.settings.theme === 'light' ? 'dark' : 'light'),
  import: () => openSheet('import'),
  export: () => exportCsv(),
  tags: () => openSheet('tags'),
  categories: () => openSheet('categories'),
  achievements: () => openSheet('achievements'),
  console: () => openSheet('console'),
  settings: () => openSheet('settings'),
  help: () => openHelp(),
}

/** One key press on the page: acted on if it is a shortcut, and then no further. */
export function onKey(event) {
  if (event.defaultPrevented || event.repeat) return
  if (state.ui.sheet || state.ui.rangeOpen || isTyping(event.target)) return
  for (const shortcut of SHORTCUTS) {
    const act = ACTIONS[shortcut.id]
    if (act && matches(event, shortcut.keys)) {
      event.preventDefault()
      count('shortcut', shortcut.id)
      act()
      return
    }
  }
}

export function startShortcuts(target = window) {
  target.addEventListener('keydown', onKey)
  return () => target.removeEventListener('keydown', onKey)
}
